import { useState } from 'react'
import { Link } from './nav'
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { adminErrorUrl } from './activityFeed'

export interface TaskError {
  id: string
  // Where the error came from: 'mcp' (Replay MCP call failure) or any other producer's tag.
  source: string
  tool: string | null
  params?: unknown | null
  recording_id?: string | null
  kind: string | null
  message: string | null
  duration_ms: number | null
  container_name: string | null
  occurred_at: string | null
  created_at: string
}

// A backend server / LLM error (backend_request_errors) recorded during the task's run, matched via
// metadata.taskId. Light shape — enough to render a clickable row; the full record loads on the
// error's own detail page. Its id is `breqerr-…`, resolved by the same /admin/errors/:id route.
export interface BackendTaskError {
  id: string
  handler_path: string | null
  http_status_code: number | null
  message: string | null
  error_source: string | null
  recording_id?: string | null
  occurred_at: string
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatDuration(ms: number | null): string {
  if (ms == null) return ''
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

// One error row, whichever source it came from — a clickable link to that error's own admin detail
// page (/admin/errors/:id), where its full record lives. Ordered by time so the combined list reads
// chronologically regardless of source.
interface Row {
  id: string
  occurredAt: string
  kind: string
  message: string
}

/**
 * Collapsible list of ALL errors recorded against the task while it ran — task_errors (Replay MCP
 * backend failures, source='mcp', plus any other source: setup-script, agent-runtime, harness, …) AND
 * backend server / LLM errors (backend_request_errors) that occurred during the run. Every row links
 * to that error's own admin detail page. Admin-only — the API omits these fields for non-admins, so
 * render this only when present. Reuses the task detail section and log styles via `classPrefix`.
 */
export default function McpErrorsSection({ errors, backendErrors = [], classPrefix }: {
  errors: TaskError[]
  backendErrors?: BackendTaskError[]
  classPrefix: string
}) {
  const [expanded, setExpanded] = useState(false)

  const rows: Row[] = [
    ...errors.map(err => ({
      id: err.id,
      occurredAt: err.occurred_at ?? err.created_at,
      kind: err.kind || 'error',
      message:
        (err.source !== 'mcp' ? `[${err.source}] ` : '') +
        (err.tool ? `${err.tool}: ` : '') +
        (err.message || (err.source === 'mcp' ? 'Unknown MCP error' : 'Unknown error')) +
        (err.duration_ms != null ? ` (${formatDuration(err.duration_ms)})` : ''),
    })),
    ...backendErrors.map(be => ({
      id: be.id,
      occurredAt: be.occurred_at,
      kind: be.error_source || (be.http_status_code != null ? `HTTP ${be.http_status_code}` : 'backend'),
      message: `[backend] ${be.message || be.handler_path || 'Server error'}`,
    })),
  ].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))

  // Keep the familiar "MCP Errors" wording while every error is an MCP task error (the common case);
  // widen to "Errors" as soon as a non-MCP source (other task source or a backend error) shows up.
  const allMcp = backendErrors.length === 0 && errors.every(e => e.source === 'mcp')
  const label = allMcp ? 'MCP Errors' : 'Errors'
  return (
    <section className={`${classPrefix}__section`}>
      <button type="button" className={`${classPrefix}__section-toggle`} onClick={() => setExpanded(!expanded)} data-analytics-event="mcp-errors-toggle">
        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <AlertTriangle size={15} /> {label} ({rows.length})
      </button>
      {expanded && (
        rows.length === 0 ? (
          <p className={`${classPrefix}__empty`}>{allMcp ? 'No MCP errors.' : 'No errors.'}</p>
        ) : (
          <div className={`${classPrefix}__logs`}>
            {/* Each error links to its own admin detail page (params, recording, full record) rather
                than crowding this row with inline detail. */}
            {rows.map(row => (
              <Link
                key={row.id}
                to={adminErrorUrl(row.id)}
                className={`${classPrefix}__log-entry`}
                style={{ textDecoration: 'none', color: 'inherit' }}
                data-testid="mcp-error-entry"
                data-analytics-event="mcp-errors-entry-open"
              >
                <span className={`${classPrefix}__log-time`}>{formatTime(row.occurredAt)}</span>
                <span className={`${classPrefix}__log-badge ${classPrefix}__log-badge--error`}>
                  <AlertTriangle size={11} />
                  {row.kind}
                </span>
                <span className={`${classPrefix}__log-message`}>{row.message}</span>
              </Link>
            ))}
          </div>
        )
      )}
    </section>
  )
}
